import { isPlatformBrowser } from '@angular/common';
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { throwError, timer } from 'rxjs';
import { timeout, retry, catchError } from 'rxjs/operators';

// SSR HTTP budget MUST be tighter than the SSR render timeout in server.ts
// (currently 4_000 ms). A single retry of 2_500 ms + 1_000 ms backoff + 2_500 ms
// would blow past the render budget and leave a zombie render burning CPU.
const SSR_TIMEOUT_MS = 2_500;
const BROWSER_TIMEOUT_MS = 12_000;

// Browser can afford one retry on transient errors. SSR cannot — see above.
const SSR_MAX_RETRIES = 0;
const BROWSER_MAX_RETRIES = 1;
const RETRY_DELAY_MS = 1_000;

function isTransientError(error: HttpErrorResponse): boolean {
  return (
    error.status === 0 || // ECONNRESET, network failures
    error.status === 502 ||
    error.status === 503 ||
    error.status === 504
  );
}

export const ssrTimeoutInterceptor: HttpInterceptorFn = (req, next) => {
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);
  const timeoutMs = isBrowser ? BROWSER_TIMEOUT_MS : SSR_TIMEOUT_MS;
  const maxRetries = isBrowser ? BROWSER_MAX_RETRIES : SSR_MAX_RETRIES;

  return next(req).pipe(
    timeout(timeoutMs),
    retry({
      count: maxRetries,
      delay: (error) => {
        if (error instanceof HttpErrorResponse && isTransientError(error)) {
          return timer(RETRY_DELAY_MS);
        }
        return throwError(() => error);
      },
    }),
    catchError((error) => {
      if (error?.name === 'TimeoutError') {
        const timeoutError = new HttpErrorResponse({
          url: req.url,
          status: 0,
          statusText: `Request timed out after ${timeoutMs}ms`,
        });
        return throwError(() => timeoutError);
      }
      return throwError(() => error);
    }),
  );
};
